/**
 * SA Tools - ROI Section
 * Calculate and visualize ROI with full configuration
 */
import React from 'react';
import { Paper, Title, Text, Stack, Alert, Divider, Table, Select, TextInput } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import ROISummaryTable from './ROISummaryTable';
import ROITimelineChart from '../charts/ROITimelineChart';
import ROIBreakEvenChart from '../charts/ROIBreakEvenChart';

function ROISection({ processes, setProcesses, contextData }) {
  // Default values for ROI fields
  const defaultValues = {
    automation_area: 'Self-service',
    automation_potential: '80%',
    number_of_interactions: '10000',
    avg_time_per_interaction: '30',
    conservative_probability: '65%',
    likely_probability: '80%',
    optimistic_probability: '95%'
  };

  // Automation area options
  const automationAreaOptions = [
    { value: 'Agent', label: 'Agent' },
    { value: 'Supervisor/Management', label: 'Supervisor/Mgmt' },
    { value: 'Operations', label: 'Operations' },
    { value: 'Self-service', label: 'Self-service' }
  ];

  // Generate automation potential options (0% to 100% in 10% intervals)
  const automationOptions = [];
  for (let i = 0; i <= 100; i += 10) {
    automationOptions.push({ value: `${i}%`, label: `${i}%` });
  }

  // Generate probability options (50% to 100% in 5% intervals)
  const probabilityOptions = [];
  for (let i = 50; i <= 100; i += 5) {
    probabilityOptions.push({ value: `${i}%`, label: `${i}%` });
  }

  // Format number with commas for display
  const formatNumberWithCommas = (value) => {
    if (!value && value !== 0) return '';
    const numStr = value.toString().replace(/,/g, '');
    const parts = numStr.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
  };

  // Remove commas from number for storage
  const parseNumberFromFormatted = (value) => {
    if (!value) return '';
    return value.toString().replace(/,/g, '');
  };

  // Calculate ROI metrics for a scenario
  const calculateScenarioMetrics = (roiData, scenarioType) => {
    // Use default values if not set in roiData
    const automationPotentialStr = roiData.automation_potential || defaultValues.automation_potential;
    const probabilityStr = roiData[`${scenarioType}_probability`] || defaultValues[`${scenarioType}_probability`];
    const interactionsStr = roiData.number_of_interactions || defaultValues.number_of_interactions;
    const avgTimeStr = roiData.avg_time_per_interaction || defaultValues.avg_time_per_interaction;

    const automationPotential = parseFloat(automationPotentialStr.replace('%', '')) / 100;
    const probability = parseFloat(probabilityStr.replace('%', '')) / 100;
    const interactions = parseInt(interactionsStr.toString().replace(/,/g, ''));
    const avgTime = parseFloat(avgTimeStr);
    const costPerAgentHour = contextData?.cost_per_agent || 25;

    const automationWithProbability = automationPotential * probability;
    const totalInteractions = Math.round(interactions * automationWithProbability);
    const totalTimeSaved = totalInteractions * avgTime; // in seconds
    const savingsPerMonth = (totalTimeSaved / 3600) * costPerAgentHour; // convert to hours and multiply by cost

    return {
      automationWithProbability: (automationWithProbability * 100).toFixed(1) + '%',
      totalInteractions: formatNumberWithCommas(totalInteractions),
      totalTimeSaved: formatNumberWithCommas(totalTimeSaved) + ' sec',
      savingsPerMonth: '$' + formatNumberWithCommas(savingsPerMonth.toFixed(2))
    };
  };

  // Handle field changes for ROI data
  const handleFieldChange = (processIndex, field, value) => {
    const newProcesses = [...processes];
    newProcesses[processIndex] = {
      ...newProcesses[processIndex],
      roi_data: {
        ...newProcesses[processIndex].roi_data,
        [field]: value
      }
    };
    setProcesses(newProcesses);
  };

  if (processes.length === 0) {
    return (
      <Paper shadow="sm" p="xl" radius="md">
        <Stack gap="md">
          <Title order={2} c="purple">ROI Analysis</Title>
          <Alert icon={<IconInfoCircle size={16} />} color="blue">
            Add processes in the "Process Assessment" tab to see ROI calculations.
          </Alert>
        </Stack>
      </Paper>
    );
  }

  return (
    <Stack gap="xl">
      <Paper shadow="sm" p="xl" radius="md">
        <Stack gap="lg">
          <div>
            <Title order={2} c="purple">ROI Analysis</Title>
            <Text size="sm" c="dimmed" mt="xs">
              Configure ROI parameters for each process and view financial projections across three scenarios.
            </Text>
          </div>

          {/* Cost Configuration Info */}
          <Alert icon={<IconInfoCircle size={16} />} color="purple" variant="light">
            <Stack gap="xs">
              <Text size="sm" fw={600}>ROI Analysis Parameters:</Text>
              <Text size="sm" component="ul" style={{ paddingLeft: '20px', margin: 0 }}>
                <li>Cost per Agent: ${formatNumberWithCommas(contextData?.cost_per_agent || 25)}/hr</li>
                <li>Professional Services: ${formatNumberWithCommas(contextData?.professional_services_price || 0)}</li>
                <li>BOM Price: ${formatNumberWithCommas(contextData?.bom_price || 0)}</li>
                <li>BOM Length: {formatNumberWithCommas(contextData?.bom_length_months || 0)} months</li>
                <li>Monthly Usage Costs: ${formatNumberWithCommas(contextData?.monthly_usage_costs || 0)}</li>
              </Text>
              <Text size="xs" c="dimmed">Update these values in the "Customer Context" tab.</Text>
            </Stack>
          </Alert>

          <Divider label="Process ROI Configuration" labelPosition="center" />

          {/* ROI Configuration Table */}
          <div style={{ overflowX: 'auto' }}>
            <Table striped highlightOnHover withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ minWidth: '100px' }}>Process ID</Table.Th>
                  <Table.Th style={{ minWidth: '150px' }}>Process Name</Table.Th>
                  <Table.Th style={{ width: '130px' }}>Automation Area</Table.Th>
                  <Table.Th style={{ width: '110px' }}>Automation Potential</Table.Th>
                  <Table.Th style={{ width: '100px' }}>Case</Table.Th>
                  <Table.Th style={{ width: '90px' }}>Probability</Table.Th>
                  <Table.Th style={{ width: '110px' }}>Automation Probability</Table.Th>
                  <Table.Th style={{ width: '120px' }}>Interactions per Month</Table.Th>
                  <Table.Th style={{ width: '120px' }}>Automated Interactions </Table.Th>
                  <Table.Th style={{ width: '100px' }}>Avg Time (in sec)</Table.Th>
                  <Table.Th style={{ width: '130px' }}>Total Time Saved</Table.Th>
                  <Table.Th style={{ width: '130px' }}>Savings/Month</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {processes.map((process, index) => {
                  const roiData = process.roi_data || {};
                  const scenarios = ['conservative', 'likely', 'optimistic'];

                  return scenarios.map((scenario, scenarioIndex) => {
                    const metrics = calculateScenarioMetrics(roiData, scenario);
                    const isFirstRow = scenarioIndex === 0;

                    return (
                      <Table.Tr key={`${process.id || index}-${scenario}`}>
                        {isFirstRow && (
                          <>
                            <Table.Td rowSpan={3} style={{ verticalAlign: 'middle', backgroundColor: '#f8f9fa' }}>
                              <Text size="sm" fw={500}>{process.id || `PROC${index + 1}`}</Text>
                            </Table.Td>
                            <Table.Td rowSpan={3} style={{ verticalAlign: 'middle', backgroundColor: '#f8f9fa' }}>
                              <Text size="sm" fw={500}>{process.name || `Process ${index + 1}`}</Text>
                            </Table.Td>
                            <Table.Td rowSpan={3} style={{ verticalAlign: 'middle', backgroundColor: '#f8f9fa' }}>
                              <Select
                                size="xs"
                                data={automationAreaOptions}
                                value={roiData.automation_area || defaultValues.automation_area}
                                onChange={(value) => handleFieldChange(index, 'automation_area', value)}
                              />
                            </Table.Td>
                            <Table.Td rowSpan={3} style={{ verticalAlign: 'middle', backgroundColor: '#f8f9fa' }}>
                              <Select
                                size="xs"
                                data={automationOptions}
                                value={roiData.automation_potential || defaultValues.automation_potential}
                                onChange={(value) => handleFieldChange(index, 'automation_potential', value)}
                              />
                            </Table.Td>
                          </>
                        )}
                        <Table.Td>
                          <Text size="xs" fw={500}>
                            {scenario.charAt(0).toUpperCase() + scenario.slice(1)} 
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Select
                            size="xs"
                            data={probabilityOptions}
                            value={roiData[`${scenario}_probability`] || defaultValues[`${scenario}_probability`]}
                            onChange={(value) => handleFieldChange(index, `${scenario}_probability`, value)}
                          />
                        </Table.Td>
                        <Table.Td style={{ backgroundColor: '#e7f5ff' }}>
                          <Text size="xs">{metrics.automationWithProbability} </Text>
                          
                        </Table.Td>
                        {isFirstRow && (
                          <Table.Td rowSpan={3} style={{ verticalAlign: 'middle', backgroundColor: '#f8f9fa' }}>
                            <TextInput
                              size="xs"
                              placeholder="10,000"
                              value={formatNumberWithCommas(roiData.number_of_interactions || defaultValues.number_of_interactions)}
                              onChange={(e) => {
                                const rawValue = parseNumberFromFormatted(e.target.value);
                                handleFieldChange(index, 'number_of_interactions', rawValue);
                              }}
                            />
                          </Table.Td>
                        )}
                        <Table.Td style={{ backgroundColor: '#e7f5ff' }}>
                          <Text size="xs">{metrics.totalInteractions}</Text>
                        </Table.Td>
                        {isFirstRow && (
                          <Table.Td rowSpan={3} style={{ verticalAlign: 'middle', backgroundColor: '#f8f9fa' }}>
                            <TextInput
                              size="xs"
                              placeholder="30"
                              value={roiData.avg_time_per_interaction || defaultValues.avg_time_per_interaction}
                              onChange={(e) => handleFieldChange(index, 'avg_time_per_interaction', e.target.value)}
                            />
                          </Table.Td>
                        )}
                        <Table.Td style={{ backgroundColor: '#e7f5ff' }}>
                          <Text size="xs">{metrics.totalTimeSaved}</Text>
                        </Table.Td>
                        <Table.Td style={{ backgroundColor: '#e7f5ff' }}>
                          <Text size="xs">{metrics.savingsPerMonth}</Text>
                        </Table.Td>
                      </Table.Tr>
                    );
                  });
                })}
              </Table.Tbody>
            </Table>
          </div>
        </Stack>
      </Paper>

      {/* ROI Summary Table */}
      <ROISummaryTable processes={processes} contextData={contextData} />

      {/* ROI Timeline Chart */}
      <ROITimelineChart processes={processes} contextData={contextData} />

      {/* ROI Break-Even Chart */}
      <ROIBreakEvenChart processes={processes} contextData={contextData} />
    </Stack>
  );
}

export default ROISection;
